import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { ChevronRight, ArrowLeft, MapPinned, ClipboardList, FolderClosed, Home } from 'lucide-react';
import { TEAM_ICONS, TEAM_COLORS } from '@/components/team/TeamIcons';
import TeamService from '@/components/team/TeamService';
import TeamInspectionsPanel from '@/components/team/TeamInspectionsPanel';
import TeamPropertyUpdates from '@/components/team/TeamPropertyUpdates';
import TeamRoutes from '@/components/team/TeamRoutes';
import TeamResources from '@/components/team/TeamResources';
import TeamSDS from '@/components/team/TeamSDS';
import SidebarShell from '@/components/wom/SidebarShell';
import { moduleIcon } from '@/lib/moduleIdentity';
import { hasCap, WS } from '@/lib/workspaces';

const API = process.env.REACT_APP_BACKEND_URL;
const { BLUE, NAVY } = TEAM_COLORS;

const T = {
  en: { brandLine: 'Total Engagement, Access & Management', comingSoon: 'Coming soon',
    enabledNo: 'TEAM access is not enabled for this account.', back: 'Back to TEAM', signOut: 'Log Out',
    home: 'Home', myRoutes: 'My Routes', routesSub: 'Assigned service-location stops',
    fieldWork: 'Field Work', fieldSub: 'Assigned work orders & field reporting',
    reuseNote: 'This connects to your existing authorized workflow. Full in-TEAM experience is being wired up.' },
  es: { brandLine: 'Total Engagement, Access & Management', comingSoon: 'Próximamente',
    enabledNo: 'El acceso a TEAM no está habilitado para esta cuenta.', back: 'Volver a TEAM', signOut: 'Cerrar sesión',
    home: 'Inicio', myRoutes: 'Mis rutas', routesSub: 'Paradas de ubicaciones asignadas',
    fieldWork: 'Trabajo de campo', fieldSub: 'Órdenes de trabajo y reportes de campo',
    reuseNote: 'Esto se conecta con tu flujo de trabajo autorizado existente. La experiencia completa dentro de TEAM se está implementando.' },
};
const labelOf = (s, lang) => (lang === 'es' ? s.label_es : s.label_en);
const subOf = (s, lang) => (lang === 'es' ? s.subtitle_es : s.subtitle_en);

const initLang = () => { try { return localStorage.getItem('portal_lang') === 'es' ? 'es' : 'en'; } catch { return 'en'; } };

const LangToggle = ({ lang, setLang }) => (
  <div className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-white/10 p-0.5 ring-1 ring-white/30" role="group" aria-label="Language / Idioma" data-testid="team-lang-toggle">
    {['en', 'es'].map((l) => (
      <button key={l} type="button" onClick={() => setLang(l)} data-testid={`team-lang-${l}`} aria-pressed={lang === l}
        className={`flex-1 rounded-full px-3 py-1.5 text-sm font-extrabold tracking-wide transition-colors ${lang === l ? 'bg-white text-[#0E2A47] shadow-sm' : 'text-white hover:bg-white/15'}`}>
        {l.toUpperCase()}
      </button>
    ))}
  </div>
);

const TeamSidebarBrand = () => (
  <div className="flex flex-col items-center border-b border-white/10 bg-white px-4 pt-5 pb-4 text-center" data-testid="team-brand">
    <img src={moduleIcon('team')} alt="TEAM" className="h-auto w-[68%] max-w-[132px] object-contain" style={{ aspectRatio: '1 / 1' }} />
    <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0E2A47]">TEAM</div>
  </div>
);

const OverviewCard = ({ s, lang, onOpen, variant = 'grid' }) => {
  const Icon = TEAM_ICONS[s.key];
  const wide = variant !== 'grid';
  if (s.key === 'sds') {
    return (
      <button data-testid="team-card-sds" onClick={() => onOpen(s)}
        className="group flex h-full flex-col items-center justify-center gap-1 rounded-2xl border-2 border-[#B91C1C] bg-[#FFD400] px-3 py-3.5 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B91C1C] lg:py-5">
        <FolderClosed size={40} className="text-[#B91C1C]" strokeWidth={2} />
        <span className="block text-lg font-extrabold leading-none text-[#B91C1C]">SDS</span>
        <span className="block text-sm font-bold leading-tight text-[#B91C1C]">Safety Data Sheets</span>
        <span className="block text-xs font-semibold leading-tight text-[#B91C1C]/90">Hojas de Datos de Seguridad</span>
      </button>
    );
  }
  if (!wide) {
    return (
      <button data-testid={`team-card-${s.key}`} onClick={() => onOpen(s)}
        className="group flex h-full flex-col items-center justify-start gap-2 rounded-2xl border border-slate-200 bg-[#FBFAF6] px-3 py-3.5 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C4FC9] lg:gap-2.5 lg:py-5">
        <span className="flex shrink-0 items-center justify-center">{Icon && <Icon size={48} />}</span>
        <span className="min-w-0">
          <span className="block break-words text-base font-bold leading-tight text-[#0E2A47]">{labelOf(s, lang)}</span>
          {s.secondary && <span className="mt-0.5 block text-xs font-semibold text-slate-400">{s.secondary}</span>}
          {subOf(s, lang) && <span className="mt-0.5 block text-xs text-slate-500">{subOf(s, lang)}</span>}
        </span>
      </button>
    );
  }
  return (
    <button data-testid={`team-card-${s.key}`} onClick={() => onOpen(s)}
      className="group flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-[#FBFAF6] px-5 py-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C4FC9]">
      <span className="flex shrink-0 items-center justify-center">{Icon && <Icon size={48} />}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-xl font-bold leading-tight text-[#0E2A47]">{labelOf(s, lang)}</span>
        {s.secondary && <span className="mt-0.5 block text-xs font-semibold text-slate-400">{s.secondary}</span>}
        {subOf(s, lang) && <span className="mt-0.5 block text-xs text-slate-500">{subOf(s, lang)}</span>}
      </span>
      <ChevronRight className="shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
};

// ---- Section stub (secured; reuse-note or Coming soon) ----
const SectionView = ({ sectionKey, lang, token, onBack }) => {
  const [data, setData] = useState(undefined); // undefined=loading, null=forbidden
  const t = T[lang];
  useEffect(() => {
    let on = true;
    axios.get(`${API}/api/team/section/${sectionKey}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => on && setData(r.data)).catch(() => on && setData(null));
    return () => { on = false; };
  }, [sectionKey, token]);
  const Icon = TEAM_ICONS[sectionKey];
  return (
    <div className="mx-auto max-w-2xl" data-testid={`team-section-${sectionKey}`}>
      <button onClick={onBack} data-testid="team-section-back" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1C4FC9]"><ArrowLeft size={16} /> {t.back}</button>
      {data === undefined && <div className="text-slate-500">…</div>}
      {data === null && <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700" data-testid="team-section-forbidden">403 — You do not have access to this section.</div>}
      {data && (
        <div className="rounded-3xl border border-slate-200 bg-[#FBFAF6] p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex justify-center">{Icon && <Icon size={64} />}</div>
          <h1 className="text-2xl font-extrabold text-[#0E2A47]">{lang === 'es' ? data.label_es : data.label_en}{data.secondary ? ` · ${data.secondary}` : ''}</h1>
          <p className="mt-3 text-sm text-slate-600">{data.status === 'reuse' ? t.reuseNote : t.comingSoon}</p>
          {data.status !== 'reuse' && (
            <span className="mt-4 inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-600">{t.comingSoon}</span>
          )}
        </div>
      )}
    </div>
  );
};

function TeamHub() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { section } = useParams();
  const [lang, setLang] = useState(initLang);
  const [state, setState] = useState({ loading: true, enabled: false, sections: [] });
  const t = T[lang];
  useEffect(() => { try { localStorage.setItem('portal_lang', lang); } catch { /* noop */ } }, [lang]);

  useEffect(() => {
    let on = true;
    axios.get(`${API}/api/team/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => on && setState({ loading: false, enabled: true, sections: r.data.sections }))
      .catch(() => on && setState({ loading: false, enabled: false, sections: [] }));
    return () => { on = false; };
  }, [token]);

  const openSection = (s) => navigate(s.route);
  const hasField = hasCap(user, WS.FIELD);

  // Not-enabled stays a standalone screen (user has no TEAM workspace).
  if (!state.loading && !state.enabled) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 p-6" data-testid="team-not-enabled">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <img src={moduleIcon('team')} alt="TEAM" className="mx-auto mb-4 h-14 w-14 object-contain" />
          <p className="text-slate-700">{t.enabledNo}</p>
          <button onClick={() => { window.location.assign('/team'); }} className="mt-5 rounded-full bg-[#0E2A47] px-5 py-2.5 text-sm font-semibold text-white">{t.signOut}</button>
        </div>
      </div>
    );
  }

  // --- Universal collapsible sidebar navigation (built from the authorized sections) ---
  const sectionNav = state.sections
    .filter((s) => s.key !== 'routes' && s.key !== 'field')
    .map((s) => ({ key: s.key, label: labelOf(s, lang), icon: TEAM_ICONS[s.key] || Home, route: s.route }));
  const navItems = [
    { key: 'overview', label: t.home, icon: Home },
    ...sectionNav,
    { key: 'routes', label: t.myRoutes, icon: MapPinned, route: '/team-hub/routes' },
    ...(hasField ? [{ key: 'field', label: t.fieldWork, icon: ClipboardList, route: '/field' }] : []),
  ];

  const activeKey = section || 'overview';
  const onNavigate = (key) => {
    if (key === 'overview') return navigate('/team-hub');
    if (key === 'field') return navigate('/field');
    if (key === 'routes') return navigate('/team-hub/routes');
    const target = navItems.find((i) => i.key === key);
    navigate(target?.route || `/team-hub/${key}`);
  };

  // White rounded module banner (kept beneath the universal header on Overview).
  const Banner = () => (
    <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="team-banner">
      <div className="relative h-[92px] sm:h-[148px]">
        <img src="/module-banners/totale-t-banner.webp" alt="" aria-hidden="true"
          className="absolute right-0 top-0 h-full w-auto max-w-none object-contain object-right" data-testid="team-banner-img" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 30%, rgba(255,255,255,0.72) 46%, rgba(255,255,255,0) 66%)' }} aria-hidden="true" />
        <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 52%, rgba(255,255,255,0.82) 64%, rgba(255,255,255,0) 84%)' }} aria-hidden="true" />
        <div className="relative z-10 flex h-full flex-col justify-center px-6">
          <h1 className="text-[24px] font-extrabold leading-tight tracking-tight sm:text-[32px]" style={{ color: NAVY }} data-testid="team-banner-title">Team</h1>
        </div>
      </div>
    </div>
  );

  const clock = state.sections.find((s) => s.key === 'clock');
  const myInfo = state.sections.find((s) => s.key === 'my_info');
  const middle = state.sections.filter((s) => s.key !== 'clock' && s.key !== 'my_info');

  const Overview = () => (
    <div data-testid="team-overview">
      <Banner />
      <div className="space-y-4">
        {clock && <OverviewCard s={clock} lang={lang} onOpen={openSection} variant="wide" />}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">{middle.map((s) => <OverviewCard key={s.key} s={s} lang={lang} onOpen={openSection} />)}</div>
        {myInfo && <div className="pt-1"><OverviewCard s={myInfo} lang={lang} onOpen={openSection} variant="wide" /></div>}
        <button onClick={() => navigate('/team-hub/routes')} data-testid="team-nav-routes" className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-[#1C4FC9]">
          <span className="rounded-lg bg-[#1C4FC9] p-2 text-white"><MapPinned size={22} /></span>
          <span><span className="block font-bold text-[#0E2A47]">{t.myRoutes}</span><span className="block text-xs text-slate-500">{t.routesSub}</span></span>
        </button>
        {hasField && (
          <button onClick={() => navigate('/field')} data-testid="team-nav-field" className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-[#5FA718]">
            <span className="rounded-lg bg-[#5FA718] p-2 text-white"><ClipboardList size={22} /></span>
            <span><span className="block font-bold text-[#0E2A47]">{t.fieldWork}</span><span className="block text-xs text-slate-500">{t.fieldSub}</span></span>
          </button>
        )}
      </div>
    </div>
  );

  const back = () => navigate('/team-hub');
  let content;
  if (state.loading) content = <div className="py-10 text-slate-500" data-testid="team-loading">…</div>;
  else if (!section) content = <Overview />;
  else if (section === 'inspections') content = <TeamInspectionsPanel onBack={back} />;
  else if (section === 'property') content = <TeamPropertyUpdates lang={lang} token={token} onBack={back} />;
  else if (section === 'resources') content = <TeamResources lang={lang} token={token} onBack={back} />;
  else if (section === 'sds') content = <TeamSDS lang={lang} token={token} onBack={back} />;
  else if (section === 'routes') content = <TeamRoutes onBack={back} />;
  else if (section === 'clock') content = <TeamService lang={lang} onBack={back} />;
  else content = <SectionView sectionKey={section} lang={lang} token={token} onBack={back} />;

  return (
    <div data-testid="team-hub">
      <SidebarShell
        navItems={navItems}
        activeKey={activeKey}
        onNavigate={onNavigate}
        brandNode={<TeamSidebarBrand />}
        workspaceLabel="TEAM"
        logoutTo="/team"
        navAccent={BLUE}
        mobileNavGrid
        mobileNavAccent={NAVY}
        bottomSlot={<LangToggle lang={lang} setLang={setLang} />}
      >
        {content}
      </SidebarShell>
    </div>
  );
}

export default TeamHub;
